import React from "react";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FORM_STYLES, S_GRID_PROPS } from "../../constants";
import { IFilterDropdown } from "../../AppState/overagesFilterFormActions";
import CustomIconComponent from "../CustomDropdownIcon";

type DateRangeFilterProps = {
  label: string;
  dateValue: string;
  fromDate: string;
  toDate: string;
  dateOptions: IFilterDropdown[];
  onDateChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  isSearching: boolean;
  dateTestId?: string;
  fromDateTestId?: string;
  toDateTestId?: string;
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label,
  dateValue,
  fromDate,
  toDate,
  dateOptions,
  onDateChange,
  onFromDateChange,
  onToDateChange,
  isSearching,
  dateTestId = "date-select",
  fromDateTestId = "from-date-picker",
  toDateTestId = "to-date-picker",
}) => {
  const handleDateSelectChange = (event: SelectChangeEvent) => {
    onFromDateChange("");
    onToDateChange("");
    onDateChange(event.target.value);
  };

  const getErrorMessage = (value: string) => {
    if (dateValue === "CUS" && !value && isSearching) {
      return <span className="form-error">Please select date</span>;
    }
    return null;
  };

  return (
    <>
      <Grid item {...S_GRID_PROPS}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true} id={`${dateTestId}-label`}>
            {label}
          </InputLabel>
          <Select
            variant="outlined"
            id={dateTestId}
            data-testid={dateTestId}
            placeholder={`Select a ${label}`}
            className="select"
            value={dateValue}
            fullWidth
            onChange={handleDateSelectChange}
            style={FORM_STYLES.LABEL}
            IconComponent={CustomIconComponent}
          >
            {dateOptions.map((obj: IFilterDropdown) => (
              <MenuItem
                style={FORM_STYLES.LABEL}
                key={obj?.actualValue}
                value={obj?.actualValue}
              >
                {obj?.displayValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {/* Conditionally render From Date and To Date */}
      {dateValue === "CUS" && (
        <>
          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={FORM_STYLES.LABEL}
                shrink={true}
                id={`${fromDateTestId}-label`}
              >
                From Date
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(fromDate ?? null)}
                  onChange={(value) => {
                    if (value?.format("YYYY-MM-DD") !== "Invalid Date") {
                      const newFromDate = value?.format("YYYY-MM-DD") ?? "";
                      onFromDateChange(newFromDate);
                      // Reset toDate if new fromDate is after the current toDate
                      if (toDate && value?.isAfter(dayjs(toDate))) {
                        onToDateChange("");
                      }
                    }
                  }}
                  format="MM/DD/YYYY"
                  disableFuture
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: false,
                      inputProps: { "data-testid": fromDateTestId },
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
            {getErrorMessage(fromDate)}
          </Grid>

          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={FORM_STYLES.LABEL}
                shrink={true}
                id={`${toDateTestId}-label`}
              >
                To Date
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(toDate ?? null)}
                  onChange={(value) => {
                    if (value?.format("YYYY-MM-DD") !== "Invalid Date") {
                      onToDateChange(value?.format("YYYY-MM-DD") ?? "");
                    }
                  }}
                  format="MM/DD/YYYY"
                  disableFuture
                  minDate={dayjs(fromDate ?? null)}
                  disabled={!fromDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: false,
                      inputProps: { "data-testid": toDateTestId },
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
            {getErrorMessage(toDate)}
          </Grid>
        </>
      )}
    </>
  );
};

export default DateRangeFilter;
